import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Tooltip } from "components/ui"
import { HiOutlineDownload, HiOutlineDocumentText, HiOutlinePlay } from "react-icons/hi"
import AttachmentPreviewDialog, {
    isImageAttachment,
    isVideoAttachment,
    isPreviewableAttachment,
} from "./AttachmentPreviewDialog"

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return null
    const n = Number(bytes)
    if (Number.isNaN(n)) return null
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// Fila para archivos no previsualizables (PDF, docs, etc): thumbnail + nombre +
// metadata + icono de descarga. Abre en pestaña nueva.
const InlineAttachment = ({ file }) => {
    const { t } = useTranslation()
    if (!file?.file) return null
    const ext = (file.extension || "").toLowerCase()
    const size = formatBytes(file.size)

    return (
        <a
            href={file.file}
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-2.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 px-2 py-1.5 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors no-underline w-full"
        >
            <span className="w-9 h-9 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 shrink-0">
                <HiOutlineDocumentText className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0 text-left">
                <p className="text-[12.5px] font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                    {file.original_name || `archivo.${ext || "file"}`}
                </p>
                {(ext || size) && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-tight mt-0.5">
                        {[ext.toUpperCase(), size].filter(Boolean).join(" · ")}
                    </p>
                )}
            </div>
            <HiOutlineDownload className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 dark:group-hover:text-indigo-300 transition-colors shrink-0" />
            <span className="sr-only">{t("download")}</span>
        </a>
    )
}

/*
 * Galeria de adjuntos unificada para actividad (timeline) y respuestas del
 * cuestionario. Las imagenes y videos se muestran como grid de thumbnails
 * cuadrados que abren un modal de preview; el resto de archivos como filas
 * descargables.
 *
 * - `onPreviewImage` (opcional): cuando el padre maneja su propio modal
 *   (ej: TicketTimeline tiene un unico AttachmentPreviewDialog para todo el
 *   feed). Si no se pasa, la galeria maneja su propio estado y modal.
 */
const TicketAttachmentGallery = ({ attachments = [], onPreviewImage }) => {
    const [internalPreview, setInternalPreview] = useState(null)

    const list = Array.isArray(attachments) ? attachments.filter((a) => a?.file) : []
    if (list.length === 0) return null

    const images = list.filter(isPreviewableAttachment)
    const files = list.filter((a) => !isPreviewableAttachment(a))

    const handlePreview = onPreviewImage || setInternalPreview

    return (
        <>
            {images.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2 max-w-md">
                    {images.map((item) => {
                        const isVideo = isVideoAttachment(item)
                        return (
                            <button
                                key={item.id || item.file}
                                type="button"
                                onClick={() => handlePreview(item)}
                                className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors"
                                title={item.original_name || ""}
                            >
                                {isVideo ? (
                                    <>
                                        <video
                                            src={item.file}
                                            preload="metadata"
                                            muted
                                            playsInline
                                            className="w-full h-full object-cover bg-black"
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                            <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
                                                <HiOutlinePlay className="w-4 h-4 text-gray-900 translate-x-[1px]" />
                                            </span>
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <img
                                            src={item.file}
                                            alt={item.original_name || "attachment"}
                                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                    </>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {files.length > 0 && (
                <div className={`flex flex-col gap-1.5 max-w-md ${images.length > 0 ? "mt-2" : "mt-3"}`}>
                    {files.map((file) => (
                        <InlineAttachment key={file.id || file.file} file={file} />
                    ))}
                </div>
            )}

            {/* Modo autonomo: la galeria maneja su propio modal cuando el padre
                no provee uno. */}
            {!onPreviewImage && (
                <AttachmentPreviewDialog
                    file={internalPreview}
                    isOpen={!!internalPreview}
                    onClose={() => setInternalPreview(null)}
                />
            )}
        </>
    )
}

export { isImageAttachment, isVideoAttachment, isPreviewableAttachment }
export default TicketAttachmentGallery
